// FULLY SEO-OPTIMIZED Home.tsx with RPM OPTIMIZATION
import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useTools } from '../context/ToolContext';
import HomeHeader from '../components/HomeHeader';
import AdSenseOptimized from '../components/Ads/AdSenseOptimized';
import ArticleAd from '../components/Ads/ArticleAd';
import HomePage from '../components/Home/HomePage';
import HomePageWithoutHero from '../components/Home/HomePageWithoutHero';
import useRPMOptimization from '../hooks/useRPMOptimization';

const createSlug = (text: string) => {
  return text.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
};

const Home: React.FC = () => {
  const { toolCategories, loading } = useTools();
  const { rpmData, isHighValuePage, estimatedRPM } = useRPMOptimization();

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="w-20 h-20 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading AI Tools...</p>
        </div>
      </div>
    );
  }

  const categoryNames = toolCategories.map((c) => c.title);
  const totalTools = toolCategories.reduce((sum, cat) => sum + cat.tools.length, 0);
  const totalCategories = toolCategories.length;
  const freeTools = toolCategories.flatMap(cat => cat.tools).filter(t => t.isFree).length;

  const firstHalfCategories = toolCategories.slice(0, 5);
  const secondHalfCategories = toolCategories.slice(5);

  const trendingTools = toolCategories
    .flatMap(cat => cat.tools.slice(0, 3))
    .slice(0, 6);

  // AI Dictionary-specific keywords for high RPM
  const aiDictionaryKeywords = [
    'AI tools directory',
    'artificial intelligence software list',
    'machine learning tools catalog',
    'ChatGPT alternatives directory',
    'best AI software 2025',
    'AI applications comparison',
    'free AI tools database',
    'enterprise AI solutions directory'
  ];

  const pageTitle = `AI Tools Directory 2025 - Compare ${totalTools}+ AI Applications | ${rpmData?.category || 'Premium AI Catalog'}`;
  const pageDescription = `World's largest AI tools directory: ${totalTools}+ verified AI applications across ${totalCategories} categories. Compare ChatGPT alternatives, AI image generators, coding assistants, and business AI tools. ${freeTools}+ free options available.`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="google-adsense-platform-account" content="ca-pub-4477070546327805" />
        <meta name="google-adsense-platform-domain" content="tooliq.ai" />

        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={aiDictionaryKeywords.join(', ')} />
        <meta name="adsense-content-type" content="ai_tools_directory" />
        <meta name="adsense-rpm-tier" content="premium" />
        <meta name="adsense-keywords" content={aiDictionaryKeywords.join(',')} />
        <link rel="canonical" href="https://tooliq.ai/" />
        
        {/* Favicons */}
        <link rel="icon" type="image/x-icon" href="/favicon.ico" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
        
        {/* Open Graph */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content="https://tooliq.ai/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://tooliq.ai/og-image.png" />
        <meta property="og:image:alt" content="TooliQ.ai - AI Tools Directory" />
        <meta property="og:site_name" content="TooliQ.ai AI Directory" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content="https://tooliq.ai/twitter-image.png" />
        <meta name="twitter:image:alt" content="TooliQ.ai AI Tools Directory" />
        
        {/* Structured Data for AI Directory */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "AI Tools Directory 2025",
            "description": pageDescription,
            "url": "https://tooliq.ai",
            "publisher": {
              "@type": "Organization",
              "name": "TooliQ.ai",
              "description": "Premium AI Tools Directory with Expert Reviews"
            },
            "about": {
              "@type": "Thing",
              "name": "Artificial Intelligence Tools",
              "description": "Software applications that use artificial intelligence and machine learning"
            },
            "keywords": aiDictionaryKeywords,
            "numberOfItems": totalTools,
            "itemListOrder": "https://schema.org/ItemListOrderDescending",
            "mainEntity": {
              "@type": "ItemList",
              "numberOfItems": totalTools,
              "itemListElement": []
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-white to-purple-50/30">
        {/* Hero Section with H1 */}
        <header className="bg-gradient-to-r from-purple-600 via-blue-600 to-purple-700 text-white py-16 relative overflow-hidden">
          {/* Background pattern for AI directory */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/10 to-transparent"></div>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-6 text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              World's Largest AI Tools Directory - 2025
            </h1>
            <p className="text-xl md:text-2xl text-purple-100 font-medium mb-8">
              Premium AI software catalog with {totalTools}+ verified applications
            </p>
            
            {/* RPM Indicator for Admins */}
            {process.env.NODE_ENV === 'development' && (
              <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full mb-6">
                <span className="text-green-300">💰</span>
                <span className="font-semibold">Est. RPM: ${estimatedRPM.toFixed(2)}</span>
                <span className="text-xs bg-green-500/30 px-2 py-0.5 rounded">Premium AI Directory</span>
              </div>
            )}
            
            <div className="flex flex-wrap justify-center gap-4 mb-6">
              <span className="bg-white/20 px-5 py-3 rounded-xl flex items-center gap-2">
                <span className="text-2xl" role="img" aria-label="database">🗃️</span>
                <span className="font-semibold">{totalTools}+ AI Tools</span>
              </span>
              <span className="bg-white/20 px-5 py-3 rounded-xl flex items-center gap-2">
                <span className="text-2xl" role="img" aria-label="categories">📂</span>
                <span className="font-semibold">{totalCategories} Categories</span>
              </span>
              <span className="bg-white/20 px-5 py-3 rounded-xl flex items-center gap-2">
                <span className="text-2xl" role="img" aria-label="verified">✅</span>
                <span className="font-semibold">Verified Listings</span>
              </span>
            </div>
            <div className="mt-8">
              <Link 
                to="/categories" 
                className="inline-block bg-white text-purple-600 font-bold px-8 py-4 rounded-xl hover:bg-purple-50 transition-colors shadow-lg text-lg hover:shadow-xl transform hover:-translate-y-1"
                aria-label="Start exploring AI tools by category"
              >
                Explore AI Directory →
              </Link>
            </div>
          </div>
        </header>

        {/* Top Ad Slot - High RPM */}
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-900 rounded-2xl p-4 border border-blue-200 dark:border-blue-800">
            <div className="text-center text-sm text-blue-600 dark:text-blue-400 mb-2 font-medium">
              🚀 Premium AI Tools Directory - High Relevance Ads
            </div>
            <AdSenseOptimized
              slot="8084827009"
              format="leaderboard"
              className="premium-home-ad"
            />
          </div>
        </div>

        <HomeHeader categories={categoryNames} toolCategories={toolCategories} />

        {/* Main SEO Content - AI Dictionary Focus */}
        <article className="max-w-6xl mx-auto px-6 py-12">
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 border border-gray-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              The Ultimate AI Tools Directory & Software Catalog
            </h2>
            
            <div className="prose max-w-none text-gray-700 space-y-6">
              <p className="text-lg">
                Welcome to the world's most comprehensive <strong>AI tools directory</strong> and <strong>artificial intelligence software catalog</strong>. 
                With <strong>{totalTools}+ verified AI applications</strong> across <strong>{totalCategories} specialized categories</strong>, 
                our platform serves as the definitive resource for discovering, comparing, and selecting the perfect AI solutions for any use case.
              </p>
              
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-xl my-8">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Why Choose Our AI Directory?</h3>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    <span><strong>Expert-Verified Listings:</strong> Every tool undergoes rigorous review</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    <span><strong>Comprehensive Comparisons:</strong> Side-by-side feature analysis</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    <span><strong>Real User Reviews:</strong> Authentic feedback from actual users</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    <span><strong>Pricing Transparency:</strong> Clear cost breakdowns and value assessment</span>
                  </li>
                </ul>
              </div>

              {/* Mid-Content Ad - High RPM */}
              <div className="my-10">
                <div className="text-center text-sm text-gray-500 mb-2">
                  <span className="inline-flex items-center gap-1">
                    <span>🤖</span>
                    Premium AI Development Tools
                  </span>
                </div>
                <AdSenseOptimized
                  slot="9014765293"
                  format="rectangle"
                  className="in-content-ad"
                />
              </div>
              
              <h3 className="text-2xl font-bold mt-8 mb-4 text-gray-900">
                AI Directory Categories & Specializations
              </h3>

              <div className="grid md:grid-cols-2 gap-8 my-8">
                <div className="bg-gradient-to-br from-purple-50 to-blue-50 p-6 rounded-xl border border-purple-200">
                  <h4 className="font-bold text-lg mb-3 text-purple-700">AI Content Creation Suite</h4>
                  <p className="mb-4">
                    Our <Link to="/categories/ai-writing" className="text-purple-600 hover:text-purple-800 font-medium">AI writing directory</Link> features 
                    <strong> {toolCategories.find(c => c.title.includes('Writing'))?.tools.length || 50}+ tools</strong> for content generation, 
                    including <Link to="/search?q=chatgpt+alternatives" className="text-purple-600 hover:text-purple-800 font-medium">ChatGPT alternatives</Link>, 
                    SEO optimizers, and multilingual content creators.
                  </p>
                  <div className="text-xs text-gray-600 bg-white/50 p-2 rounded-lg">
                    <span className="font-medium">High RPM Category:</span> AI writing tools attract premium advertisers
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-blue-50 to-green-50 p-6 rounded-xl border border-blue-200">
                  <h4 className="font-bold text-lg mb-3 text-blue-700">AI Development Ecosystem</h4>
                  <p className="mb-4">
                    Explore <Link to="/categories/coding-assistants" className="text-blue-600 hover:text-blue-800 font-medium">{toolCategories.find(c => c.title.includes('Coding'))?.tools.length || 40}+ developer tools</Link>, 
                    including <Link to="/search?q=github+copilot" className="text-blue-600 hover:text-blue-800 font-medium">GitHub Copilot competitors</Link>, 
                    automated testing platforms, and AI-powered code review systems.
                  </p>
                  <div className="text-xs text-gray-600 bg-white/50 p-2 rounded-lg">
                    <span className="font-medium">Developer-Focused Ads:</span> High-value B2B advertising
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8 my-8">
                <div className="bg-gradient-to-br from-pink-50 to-red-50 p-6 rounded-xl border border-pink-200">
                  <h4 className="font-bold text-lg mb-3 text-pink-700">Visual AI & Design Tools</h4>
                  <p className="mb-4">
                    Browse <strong>{toolCategories.find(c => c.title.includes('Image') || c.title.includes('Design'))?.tools.length || 60}+ visual AI tools</strong> 
                    including <Link to="/search?q=midjourney+alternatives" className="text-pink-600 hover:text-pink-800 font-medium">Midjourney alternatives</Link>, 
                    video editors, and 3D model generators. Perfect for designers, marketers, and content creators.
                  </p>
                  <div className="text-xs text-gray-600 bg-white/50 p-2 rounded-lg">
                    <span className="font-medium">Creative Market:</span> Design software advertisers pay premium rates
                  </div>
                </div>

                <div className="bg-gradient-to-br from-yellow-50 to-orange-50 p-6 rounded-xl border border-yellow-200">
                  <h4 className="font-bold text-lg mb-3 text-orange-700">Business AI Solutions</h4>
                  <p className="mb-4">
                    Discover <strong>{toolCategories.find(c => c.title.includes('Business') || c.title.includes('Productivity'))?.tools.length || 45}+ enterprise AI tools</strong> 
                    for automation, analytics, customer service, and workflow optimization. Transform your business operations.
                  </p>
                  <div className="text-xs text-gray-600 bg-white/50 p-2 rounded-lg">
                    <span className="font-medium">B2B Premium:</span> Enterprise software commands highest CPMs
                  </div>
                </div>
              </div>
              
              <h3 className="text-2xl font-bold mt-8 mb-4 text-gray-900">
                How to Use Our AI Tools Directory
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 my-8">
                <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <span className="text-xl">🔍</span>
                  </div>
                  <h4 className="font-bold text-lg mb-2">1. Search & Filter</h4>
                  <p className="text-sm text-gray-600">Use our advanced search to find tools by category, pricing, features, or use case.</p>
                </div>
                
                <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                    <span className="text-xl">📊</span>
                  </div>
                  <h4 className="font-bold text-lg mb-2">2. Compare Features</h4>
                  <p className="text-sm text-gray-600">Side-by-side comparison tables show pricing, features, and user ratings.</p>
                </div>
                
                <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                    <span className="text-xl">✅</span>
                  </div>
                  <h4 className="font-bold text-lg mb-2">3. Make Decision</h4>
                  <p className="text-sm text-gray-600">Read expert reviews and user feedback to select the perfect tool.</p>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* Premium Categories Section */}
        <section className="max-w-6xl mx-auto px-6 py-12">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Premium AI Tool Categories
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Explore our most popular AI tool categories with premium contextual advertising
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {firstHalfCategories.map((category, index) => {
              const isHighValue = ['Writing', 'Coding', 'Image', 'Business', 'Video'].some(keyword => 
                category.title.includes(keyword)
              );
              
              return (
                <Link 
                  key={index}
                  to={`/categories/${createSlug(category.title)}`}
                  className={`group p-6 rounded-2xl border-2 transition-all duration-300 ${
                    isHighValue 
                      ? 'bg-gradient-to-br from-white to-blue-50 border-blue-200 hover:border-blue-400 hover:shadow-xl' 
                      : 'bg-white border-gray-200 hover:border-purple-300 hover:shadow-lg'
                  }`}
                  aria-label={`Explore ${category.title} AI tools`}
                >
                  {isHighValue && (
                    <div className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-3 py-1 rounded-full mb-3">
                      <span>💰</span>
                      <span>Premium Category</span>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-purple-600 transition-colors">
                      {category.title}
                    </h3>
                    <span className="text-2xl" role="img" aria-label="robot">
                      {isHighValue ? '🚀' : '🤖'}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <span>{category.tools.length} tools</span>
                    <span>
                      {category.tools.filter(t => t.isFree).length} free • 
                      {category.tools.filter(t => !t.isFree).length} paid
                    </span>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {category.tools.slice(0, 3).map((tool, toolIndex) => (
                      <span key={toolIndex} className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                        {tool.title}
                      </span>
                    ))}
                  </div>
                  
                  <div className={`font-medium flex items-center gap-2 group-hover:gap-3 transition-all ${
                    isHighValue ? 'text-blue-600' : 'text-purple-600'
                  }`}>
                    <span>Browse {category.tools.length} Tools</span>
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </div>
                </Link>
              );
            })}
          </div>
          
          {/* Center Ad between categories */}
          <div className="my-10">
            <AdSenseOptimized
              slot="8768187433"
              format="leaderboard"
              className="center-ad"
            />
          </div>
          
          <div className="text-center">
            <Link 
              to="/categories" 
              className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold px-8 py-4 rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              aria-label="View all AI tool categories"
            >
              <span>Explore All {totalCategories} Categories</span>
              <span>→</span>
            </Link>
          </div>
        </section>

        {/* Trending AI Tools Section */}
        <section className="max-w-6xl mx-auto px-6 py-12">
          <div className="bg-gradient-to-br from-white to-blue-50 rounded-3xl p-8 shadow-xl border border-blue-100">
            <div className="flex flex-col md:flex-row justify-between items-center mb-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900">
                  Trending AI Tools This Week
                </h2>
                <p className="text-gray-600 mt-2">
                  Most viewed and searched AI applications in our directory
                </p>
              </div>
              {process.env.NODE_ENV === 'development' && (
                <div className="mt-4 md:mt-0">
                  <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full">
                    <span>📈</span>
                    <span className="font-medium">High Engagement Section</span>
                  </div>
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {trendingTools.map((tool, index) => {
                const isHighValueTool = tool.title.toLowerCase().includes('chatgpt') || 
                                       tool.title.toLowerCase().includes('midjourney') ||
                                       tool.title.toLowerCase().includes('copilot');
                
                return (
                  <div key={index} className={`p-6 rounded-xl border transition-colors ${
                    isHighValueTool 
                      ? 'bg-gradient-to-br from-white to-green-50 border-green-200' 
                      : 'bg-white border-gray-200'
                  } hover:border-blue-300`}>
                    {isHighValueTool && (
                      <div className="inline-flex items-center gap-1 bg-green-100 text-green-800 text-xs px-3 py-1 rounded-full mb-4">
                        <span>🔥</span>
                        <span>Top Trending</span>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        isHighValueTool 
                          ? 'bg-gradient-to-br from-green-100 to-blue-100' 
                          : 'bg-gradient-to-br from-blue-100 to-purple-100'
                      }`}>
                        <span className="text-xl">
                          {isHighValueTool ? '⚡' : '🤖'}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-bold text-lg text-gray-900">{tool.title}</h3>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            tool.isFree 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {tool.isFree ? 'Free' : 'Paid'}
                          </span>
                          <span className="text-sm text-gray-500">{tool.category}</span>
                        </div>
                      </div>
                    </div>
                    
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {tool.description || 'Powerful AI tool for various applications'}
                    </p>
                    
                    <div className="flex justify-between items-center">
                      <Link 
                        to={`/tools/${createSlug(tool.title)}/${createSlug(tool.title)}`}
                        className="text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center gap-1"
                        aria-label={`View details for ${tool.title}`}
                      >
                        View Details →
                      </Link>
                      
                      {isHighValueTool && (
                        <span className="text-xs text-green-600 font-medium">
                          High RPM Content
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* In-section ad */}
            <div className="mt-10">
              <ArticleAd 
              />
            </div>
            
            <div className="text-center mt-8">
              <Link 
                to="/trending" 
                className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-800 font-bold"
                aria-label="View all trending AI tools"
              >
                <span>View All Trending Tools</span>
                <span>→</span>
              </Link>
            </div>
          </div>
        </section>

        {/* Bottom CTA with RPM Stats */}
        <section className="max-w-4xl mx-auto px-6 py-12 mb-16">
          <div className="bg-gradient-to-r from-purple-600 via-blue-600 to-purple-700 text-white rounded-3xl p-10 text-center shadow-2xl relative overflow-hidden">
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/10 to-transparent"></div>
            </div>
            
            <div className="relative">
              <h2 className="text-3xl font-bold mb-4">
                Join {totalTools.toLocaleString()}+ AI Tool Users
              </h2>
              
              <p className="text-xl text-purple-100 mb-8 max-w-2xl mx-auto">
                Access the world's largest AI tools directory with premium contextual advertising
              </p>
              
              {/* RPM Stats for Admins */}
              {process.env.NODE_ENV === 'development' && (
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-8 max-w-md mx-auto">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-300">${estimatedRPM.toFixed(2)}</div>
                      <div className="text-purple-200">Est. RPM</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-300">{totalCategories}</div>
                      <div className="text-purple-200">AI Categories</div>
                    </div>
                  </div>
                  <div className="text-xs text-purple-300 mt-2 text-center">
                    Premium AI directory with high-value contextual ads
                  </div>
                </div>
              )}
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/categories" 
                  className="inline-block bg-white text-purple-600 font-bold px-8 py-4 rounded-xl hover:bg-purple-50 transition-colors shadow-lg text-lg transform hover:-translate-y-0.5"
                  aria-label="Browse all AI tool categories"
                >
                  Browse All Categories
                </Link>
                <Link 
                  to="/submit-tool" 
                  className="inline-block bg-purple-700 text-white font-bold px-8 py-4 rounded-xl hover:bg-purple-800 transition-colors shadow-lg text-lg"
                  aria-label="Submit your AI tool to our directory"
                >
                  Submit Your Tool
                </Link>
              </div>
              
              <div className="mt-8 pt-8 border-t border-purple-400/30">
                <p className="text-purple-100 mb-4">AI Directory Resources</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <Link to="/blog/ai-tools-guide" className="text-purple-200 hover:text-white transition-colors">Getting Started Guide</Link>
                  <Link to="/comparisons" className="text-purple-200 hover:text-white transition-colors">Tool Comparisons</Link>
                  <Link to="/pricing-guide" className="text-purple-200 hover:text-white transition-colors">Pricing Guide</Link>
                  <Link to="/api" className="text-purple-200 hover:text-white transition-colors">API Access</Link>
                  <Link to="/enterprise" className="text-purple-200 hover:text-white transition-colors">Enterprise Solutions</Link>
                  <Link to="/affiliate" className="text-purple-200 hover:text-white transition-colors">Affiliate Program</Link>
                  <Link to="/research" className="text-purple-200 hover:text-white transition-colors">AI Research</Link>
                  <Link to="/newsletter" className="text-purple-200 hover:text-white transition-colors">Industry Newsletter</Link>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Bottom Leaderboard Ad */}
        <div className="max-w-6xl mx-auto px-6 mb-10">
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-4 border border-blue-200">
            <AdSenseOptimized
              slot="7845153853"
              format="leaderboard"
              className="footer-leaderboard-ad"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Home;